import React, { useEffect } from "react";
import { Card, CardBody, Col, TabContent } from "reactstrap";

import { useCurrentUser, useSetCurrentUser } from "../../../slices/auth/hooks";
import OnboardingSideBar from "./SideSteps/OnboardingSideBar";

import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import { authMe, logoutUser } from "../../../slices/thunks";

import { PrimaryLogo } from "../../../Components/Logo/PrimaryLogo";
import { DASHBOARD_USER_ROLES, onboardingStepsEnum } from "../../../constants";
import { getRolesForTables } from "../../../slices/admin/thunk";
import {
  getOnboardingProgress,
  onboardingFinish,
} from "../../../slices/general/thunk";
import AgreementsStep from "./Steps/AgreementsStep";
import EmployerEmployeesStep from "./Steps/EmployerEmployeesStep";
import PaymentNewStep from "./Steps/PaymentNewStep";
import ProfileStep from "./Steps/ProfileStep";
import ProfileStepBroker from "./Steps/ProfileStepBroker";
import WelcomeStep from "./Steps/WelcomeStep";

const AuthSteps = () => {
  const currentUser = useCurrentUser();
  const setCurrentUser = useSetCurrentUser();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeVerticalTab, setActiveVerticalTab] = React.useState(
    onboardingStepsEnum.WelcomeStep
  );

  const [isCompleted, setIsCompleted] = React.useState(false);
  const [loadingUpdate, setLoadingUpdate] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [employees, setEmployees] = React.useState([]);
  const [onboardingProgress, setOnboardingProgress] = React.useState(null);
  const [employerCoverageEffectiveDates, setEmployerCoverageEffectiveDates] =
    React.useState(null);

  const [employerCoverageEffectiveDate, setEmployerCoverageEffectiveDate] =
    React.useState(null);

  const step = searchParams.get("step");
  const status = searchParams.get("status");

  const isProfileStepAlreadyCompleted =
    onboardingProgress?.steps?.profile === true;
  const isPaymentStepAlreadyCompleted =
    onboardingProgress?.steps?.payment === true;

  useEffect(() => {
    if (step === "payment" && (status === "refresh" || status === "return")) {
      toggleVerticalTab(onboardingStepsEnum.PaymentStep);
    }
  }, [step, status]);

  useEffect(() => {
    // Initialize

    const init = async () => {
      // Get onboarding progress
      const onbProgress = await handleGetOnboardingProgress();

      const isProfileStepAlreadyCompleted =
        onbProgress?.steps?.profile === true;
      const isPaymentStepAlreadyCompleted =
        onbProgress?.steps?.payment === true;

      // Do initial redirections based on onboarding progress
      if (isProfileStepAlreadyCompleted) {
        if (isPaymentStepAlreadyCompleted) {
          if (currentUser.role === DASHBOARD_USER_ROLES.EMPLOYER) {
            toggleVerticalTab(onboardingStepsEnum.EmployerEmployeesStep);
          } else {
            finishOnboarding();
          }
        } else {
          toggleVerticalTab(onboardingStepsEnum.PaymentStep);
        }
      }
    };

    init();
  }, []);

  // Each time a stpe changes, get onboarding progress.
  useEffect(() => {
    handleGetOnboardingProgress();
  }, [activeVerticalTab]);

  const toggleVerticalTab = (tab) => {
    if (activeVerticalTab !== tab) {
      setActiveVerticalTab(tab);
    }

    const previestab = document.querySelector(".previestab");
    if (previestab && activeVerticalTab === tab - 1) {
      previestab.style.display = "block";
    } else if (previestab) {
      previestab.style.display = "none";
    }
  };

  const handleLogout = () => {
    dispatch(logoutUser());
    localStorage.clear();
  };

  const handleGetOnboardingProgress = async () => {
    try {
      const response = await dispatch(getOnboardingProgress()).unwrap();
      if (response.error) {
        console.log(response.error);
        return;
      }

      setOnboardingProgress(response);

      if (response?.coverageEffectiveDate) {
        setEmployerCoverageEffectiveDate(response.coverageEffectiveDate);
      }

      if (
        response?.completed &&
        currentUser.role !== DASHBOARD_USER_ROLES.EMPLOYER
      ) {
        setIsCompleted(response.completed);
      }

      return response;
    } catch (error) {
      console.log(error);
    }
  };

  const finishOnboarding = async () => {
    try {
      setLoadingUpdate(true);
      Swal.fire({
        title: "Please wait...",
        html: "Your information is being processed.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
      const response = await dispatch(onboardingFinish()).unwrap();

      if (response.error) {
        Swal.fire({
          title: "Oops!",
          text: response.error,
          icon: "error",
          showConfirmButton: false,
          timer: 3000,
        });
        setLoadingUpdate(false);
        return;
      } else if (response.completed) {
        // Update user data
        setCurrentUser({
          ...currentUser,
          onboardingCompleted: true,
        });

        setIsCompleted(response.completed);
        setLoadingUpdate(false);

        // Close the modal
        Swal.close();

        return;
      } else {
        Swal.close();
      }
    } catch (error) {
      setLoadingUpdate(true);
      console.log(error);
      setLoadingUpdate(false);

      Swal.close();
    }
  };

  const handleRefreshEmployees = () => {
    fetchEmployees();
  };

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await dispatch(
        getRolesForTables({
          id: currentUser.id,
          userRole: DASHBOARD_USER_ROLES.EMPLOYER,
          role: "employees",
          status: "pending",
          page: 0,
        })
      ).unwrap();
      setEmployees(response.data);
      setLoading(false);
    } catch (error) {
      setLoading(true);
      console.log(error);
      setLoading(false);
    }
  };

  const authMeData = async () => {
    try {
      const response = await dispatch(authMe());

      if (!response || response.error) {
        throw new Error("Error getting document:", response.error);
      }

      return response;
    } catch (error) {
      console.log(error);
      Swal.fire({
        title: "Error!",
        text: "Something went wrong!",
        icon: "error",
        confirmButtonText: "Ok",
      });
      throw error;
    }
  };

  useEffect(() => {
    const handleVerifyUser = async () => {
      if (isCompleted) {
        setCurrentUser({ ...currentUser, onboardingCompleted: true });
        Swal.fire({
          title: "Success!",
          text: "Onboarding completed!",
          icon: "success",
          showConfirmButton: false,
          timer: 1500,
        });
        try {
          const response = await authMeData();

          setCurrentUser({
            ...response.payload,
            onboardingCompleted: true,
          });
          if (
            currentUser.role === DASHBOARD_USER_ROLES.GA ||
            currentUser.role === DASHBOARD_USER_ROLES.BROKER
          ) {
            navigate("/profile?onboarding-completed=true");
          } else {
            navigate("/profile");
          }
        } catch (error) {
          console.error(error);
          Swal.fire({
            title: "Error!",
            text: "Something went wrong!",
            icon: "error",
            confirmButtonText: "Ok",
          });
        }
      }
    };

    handleVerifyUser();
  }, [isCompleted]);

  console.log("activeVerticalTab", activeVerticalTab);

  return (
    <React.Fragment>
      <div className="page-content pt-0 ">
        <div className="my-3">
          <PrimaryLogo />
        </div>
        <Col xs={12}>
          <OnboardingSideBar
            currentUser={currentUser}
            toggleVerticalTab={toggleVerticalTab}
            activeVerticalTab={activeVerticalTab}
          />
        </Col>
        {activeVerticalTab !== onboardingStepsEnum.WelcomeStep && (
          <Card className="shadow-none mt-3 mb-3">
            <div className="p-3 d-flex align-items-center">
              <div className="d-flex flex-column">
                <h3 className=" fw-semibold  text-dark d-flex align-items-center">
                  Please fill all information to continue
                </h3>
                <span className="text-muted">
                  Here are your{" "}
                  {activeVerticalTab === onboardingStepsEnum.StepOne
                    ? "company"
                    : activeVerticalTab === onboardingStepsEnum.PaymentStep
                      ? "payment"
                      : activeVerticalTab ===
                        onboardingStepsEnum.EmployerEmployeesStep
                        ? "employee"
                        : "profile"}
                  {" details "}
                </span>
              </div>
              <div className="mb-3 justify-content-end ms-auto d-flex">
                <button
                  onClick={handleLogout}
                  className="btn btn-soft-primary border border-primary py-1"
                >
                  <i className="mdi mdi-logout fs-16 align-middle me-1 "></i>{" "}
                  <span className="align-middle" data-key="t-logout">
                    Logout
                  </span>
                </button>
              </div>
            </div>
          </Card>
        )}

        <div>
          <Card className="mt-3">
            <CardBody>
              <TabContent activeTab={activeVerticalTab}>
                <WelcomeStep
                  welcomeStep={onboardingStepsEnum.WelcomeStep}
                  currentUser={currentUser}
                  toggleVerticalTab={toggleVerticalTab}
                  activeVerticalTab={activeVerticalTab}
                />
                {currentUser.role === DASHBOARD_USER_ROLES.BROKER ? (
                  <ProfileStepBroker
                    profileStepBroker={onboardingStepsEnum.StepOneBroker}
                    setCurrentUser={setCurrentUser}
                    currentUser={currentUser}
                    toggleVerticalTab={toggleVerticalTab}
                    activeVerticalTab={activeVerticalTab}
                  />
                ) : (
                  <ProfileStep
                    profileStep={onboardingStepsEnum.StepOne}
                    setCurrentUser={setCurrentUser}
                    currentUser={currentUser}
                    toggleVerticalTab={toggleVerticalTab}
                    activeVerticalTab={activeVerticalTab}
                  />
                )}
                <PaymentNewStep
                  paymentStep={onboardingStepsEnum.PaymentStep}
                  onFinishOnboarding={finishOnboarding}
                  toggleVerticalTab={toggleVerticalTab}
                  activeVerticalTab={activeVerticalTab}
                />

                {currentUser.role === DASHBOARD_USER_ROLES.EMPLOYER && (
                  <EmployerEmployeesStep
                    employerEmployeesStep={
                      onboardingStepsEnum.EmployerEmployeesStep
                    }
                    activeVerticalTab={activeVerticalTab}
                    employees={employees}
                    loading={loading}
                    setLoading={setLoading}
                    setEmployees={setEmployees}
                    onRefreshTable={handleRefreshEmployees}
                    loadingUpdate={loadingUpdate}
                    onFinishOnboarding={finishOnboarding}
                    isProfileCompleted={isCompleted}
                    currentUser={currentUser}
                    coverageEffectiveDate={employerCoverageEffectiveDate}
                    toggleVerticalTab={toggleVerticalTab}
                  />
                )}

                {currentUser.role !== DASHBOARD_USER_ROLES.NATIONAL && (
                  <AgreementsStep
                    agreementStep={onboardingStepsEnum.AgreementStep}
                    currentUser={currentUser}
                    toggleVerticalTab={toggleVerticalTab}
                    activeVerticalTab={activeVerticalTab}
                    onFinishOnboarding={finishOnboarding}
                  />
                )}
              </TabContent>
            </CardBody>
          </Card>
        </div>
      </div>
    </React.Fragment>
  );
};

export default AuthSteps;
